import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { getProducts, resolveImageUrl } from '../../services/api';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import {
  TruckIcon, ShieldCheckIcon, ClockIcon, SparklesIcon, ArrowRightIcon,
  DevicePhoneMobileIcon, ChevronRightIcon, HeartIcon, MapPinIcon, StarIcon,
  UserGroupIcon, CurrencyRupeeIcon, ShoppingBagIcon, CubeIcon, BoltIcon,
  GiftIcon, Bars3Icon, XMarkIcon, MagnifyingGlassIcon, ShoppingCartIcon,
  UserIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const EMOJI_MAP = { Milk:'🥛', Curd:'🥛', Paneer:'🧀', Ghee:'🫙', Butter:'🧈', Buttermilk:'🧃' };

/* ── Search overlay ──────────────────────────────────────────────────────────── */
const SearchOverlay = ({ products, onClose, navigate }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);
  const results = query.trim() ? products.filter(p =>
    p.name?.toLowerCase().includes(query.toLowerCase()) ||
    p.category?.toLowerCase().includes(query.toLowerCase())
  ) : [];
  return (
    <>
      <style>{`
        .sr-ov{position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(6px);z-index:300;display:flex;flex-direction:column;align-items:center;padding:1rem;padding-top:4.5rem;}
        .sr-bx{background:white;border-radius:1.5rem;width:100%;max-width:560px;overflow:hidden;box-shadow:0 30px 60px rgba(0,0,0,.25);animation:srIn .25s ease-out;}
        @keyframes srIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}
        .sr-top{display:flex;align-items:center;gap:.75rem;padding:.9rem 1.1rem;border-bottom:1px solid #f3f4f6;}
        .sr-inp{flex:1;border:none;outline:none;font-size:1rem;font-weight:600;color:#111827;font-family:'Nunito',sans-serif;background:transparent;}
        .sr-inp::placeholder{color:#9ca3af;font-weight:400;}
        .sr-cl{background:#f3f4f6;border:none;border-radius:50%;width:1.85rem;height:1.85rem;display:flex;align-items:center;justify-content:center;cursor:pointer;}
        .sr-res{max-height:340px;overflow-y:auto;}
        .sr-empty{padding:2rem;text-align:center;color:#9ca3af;font-size:.9rem;}
        .sr-hint{padding:.9rem 1.1rem;font-size:.75rem;color:#9ca3af;font-weight:600;text-transform:uppercase;letter-spacing:.4px;}
        .sr-it{display:flex;align-items:center;gap:.9rem;padding:.8rem 1.1rem;cursor:pointer;transition:background .18s;border:none;background:none;width:100%;text-align:left;font-family:'Nunito',sans-serif;}
        .sr-it:hover{background:#f0fdf4;}
        .sr-img{width:2.8rem;height:2.8rem;border-radius:.6rem;background:#f9fafb;border:1px solid #f3f4f6;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;}
        .sr-img img{width:100%;height:100%;object-fit:contain;}
        .sr-nm{font-size:.88rem;font-weight:700;color:#111827;} .sr-cat{font-size:.72rem;color:#6b7280;}
        .sr-pr{font-size:.9rem;font-weight:800;color:#059669;flex-shrink:0;}
        .sr-cnt{padding:.65rem 1.1rem;background:#f0fdf4;border-top:1px solid #d1fae5;}
        .sr-cnt-btn{width:100%;background:linear-gradient(135deg,#10b981,#059669);color:white;border:none;padding:.65rem;border-radius:.75rem;font-weight:700;font-size:.88rem;cursor:pointer;font-family:'Nunito',sans-serif;display:flex;align-items:center;justify-content:center;gap:.4rem;}
      `}</style>
      <div className="sr-ov" onClick={onClose}>
        <div className="sr-bx" onClick={e=>e.stopPropagation()}>
          <div className="sr-top">
            <MagnifyingGlassIcon style={{width:'1.15rem',height:'1.15rem',color:'#059669',flexShrink:0}}/>
            <input ref={inputRef} className="sr-inp" placeholder="Search milk, paneer, curd…" value={query} onChange={e=>setQuery(e.target.value)}/>
            <button className="sr-cl" onClick={onClose}><XMarkIcon style={{width:'.88rem',height:'.88rem'}}/></button>
          </div>
          <div className="sr-res">
            {!query.trim()&&<div className="sr-hint">🔥 Popular: Nadan Milk, Paneer, Curd, Buttermilk</div>}
            {query.trim()&&results.length===0&&<div className="sr-empty">😔 No results for "<strong>{query}</strong>"</div>}
            {results.slice(0,8).map(p=>(
              <button key={p.id} className="sr-it" onClick={()=>{navigate('/products');onClose();}}>
                <div className="sr-img">
                  {p.image_url?<img src={p.image_url} alt={p.name} onError={e=>{e.target.style.display='none';}}/>
                    :<span style={{fontSize:'1.35rem'}}>{EMOJI_MAP[p.category]||'🥛'}</span>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div className="sr-nm">{p.name}</div>
                  <div className="sr-cat">{p.category||'Dairy'}</div>
                </div>
                <div className="sr-pr">₹{Number(p.price).toFixed(2)}</div>
              </button>
            ))}
          </div>
          {results.length>0&&(
            <div className="sr-cnt">
              <button className="sr-cnt-btn" onClick={()=>{navigate('/products');onClose();}}>
                <ShoppingBagIcon style={{width:'1rem',height:'1rem'}}/>View all {results.length} results
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ── Profile Dropdown ────────────────────────────────────────────────────────── */
const ProfileDropdown = ({ user, onLogout, onViewProfile, onClose }) => (
  <>
    <style>{`
      .pd-w{position:absolute;top:calc(100% + 10px);right:0;background:white;border-radius:1.25rem;padding:.4rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;min-width:210px;z-index:200;animation:pdIn .2s ease-out;}
      @keyframes pdIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
      .pd-hd{padding:.75rem 1rem;border-bottom:1px solid #f3f4f6;margin-bottom:.25rem;}
      .pd-av{width:2.5rem;height:2.5rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:1rem;font-weight:800;margin-bottom:.4rem;}
      .pd-nm{font-weight:800;font-size:.92rem;color:#111827;} .pd-em{font-size:.72rem;color:#6b7280;margin-top:.1rem;}
      .pd-it{display:flex;align-items:center;gap:.55rem;padding:.55rem .9rem;border-radius:.65rem;font-size:.85rem;font-weight:600;color:#374151;cursor:pointer;transition:background .18s;border:none;background:none;width:100%;font-family:'Nunito',sans-serif;}
      .pd-it:hover{background:#f0fdf4;color:#059669;} .pd-it.dng:hover{background:#fff1f2;color:#ef4444;}
      .pd-it svg{width:.95rem;height:.95rem;flex-shrink:0;}
    `}</style>
    <div className="pd-w">
      <div className="pd-hd">
        <div className="pd-av">{(user.name||'U').charAt(0).toUpperCase()}</div>
        <div className="pd-nm">{user.name||'Customer'}</div>
        <div className="pd-em">📱 +{user.phone}</div>
      </div>
      <button className="pd-it" onClick={onViewProfile}><ShoppingBagIcon/>My Orders</button>
      <button className="pd-it" onClick={onViewProfile}><UserCircleIcon/>My Profile</button>
      <button className="pd-it dng" onClick={onLogout}><ArrowRightOnRectangleIcon/>Logout</button>
    </div>
  </>
);

/* ── Home component ──────────────────────────────────────────────────────────── */
const Home = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { addToCart: ctxAddToCart, cartCount } = useCart();
  const { customer: currentUser, isLoggedIn, logout } = useCustomerAuth();

  const [scrolled,         setScrolled]         = useState(false);
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);
  const [activeTestimonial,setActiveTestimonial]= useState(0);
  const [addedMap,         setAddedMap]         = useState({});
  const [imgErrors,        setImgErrors]        = useState({});
  const [showAuthModal,    setShowAuthModal]    = useState(false);
  const [showProfileDrop,  setShowProfileDrop]  = useState(false);
  const [showProfile,      setShowProfile]      = useState(false);
  const [showSearch,       setShowSearch]       = useState(false);
  const [products,         setProducts]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [loadError,        setLoadError]        = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError(false);
    getProducts().then(r => {
      if (cancelled) return;
      const data = r?.data?.products;
      if (Array.isArray(data) && data.length > 0) {
        setProducts(data.map(p => ({
          ...p,
          priceValue:   Number(p.price),
          displayPrice: `₹${Number(p.price).toFixed(2)}`,
          desc:         p.description || '',
        })));
      }
      setLoading(false);
    }).catch(() => { if (!cancelled) { setLoadError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { const t = setInterval(() => setActiveTestimonial(p=>(p+1)%3), 3500); return ()=>clearInterval(t); }, []);
  useEffect(() => { const fn=()=>setScrolled(window.scrollY>20); window.addEventListener('scroll',fn); return ()=>window.removeEventListener('scroll',fn); }, []);
  useEffect(() => {
    if (!showProfileDrop) return;
    const fn=()=>setShowProfileDrop(false);
    setTimeout(()=>document.addEventListener('click',fn),0);
    return ()=>document.removeEventListener('click',fn);
  }, [showProfileDrop]);
  useEffect(() => {
    const fn=(e)=>{ if(e.key==='Escape'){setShowSearch(false);setMobileMenuOpen(false);setShowProfile(false);setShowAuthModal(false);} };
    window.addEventListener('keydown',fn); return ()=>window.removeEventListener('keydown',fn);
  }, []);

  const handleAuthSuccess = (user) => { setShowAuthModal(false); toast.success(`Welcome, ${user.name||'there'}! 🥛`); };
  const handleLogout = () => { logout(); setShowProfileDrop(false); toast.success('Logged out 👋'); };

  const handleAddToCart = useCallback((product, e) => {
    if (e) e.stopPropagation();
    ctxAddToCart({ ...product, price: product.priceValue ?? Number(product.price) });
    setAddedMap(prev => ({ ...prev, [product.id]: true }));
    setTimeout(() => setAddedMap(prev => ({ ...prev, [product.id]: false })), 1800);
    toast.success(`${product.name} added to cart! 🛒`);
  }, [ctxAddToCart]);

  const handleBuyNow = useCallback((product, e) => {
    if (e) e.stopPropagation();
    ctxAddToCart({ ...product, price: product.priceValue ?? Number(product.price) });
    navigate('/checkout');
  }, [ctxAddToCart, navigate]);

  const getImageSrc = (p) => { const raw=p?.image_url||p?.imageUrl||p?.image||null; return resolveImageUrl?resolveImageUrl(raw):raw; };
  const getEmoji    = (p) => p.emoji || EMOJI_MAP[p.category] || '🥛';
  const handleImgError = (key) => setImgErrors(prev => ({ ...prev, [key]: true }));

  const testimonials = [
    { name:'Priya Sharma',  location:'Kakkanad, Ernakulam',    text:'Kerala\'s best nadan milk! Pure and fresh every morning.',  image:'👩' },
    { name:'Rahul Verma',   location:'Thrippunithura, Kochi',  text:'Best nadan dairy products online. Pure, fresh, great prices!', image:'👨' },
    { name:'Anita Desai',   location:'Edapally, Kochi',        text:'Always fresh, love the 24/7 delivery availability!',        image:'👩' },
  ];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    html{-webkit-text-size-adjust:100%;width:100%;}
    body{font-family:'Nunito',sans-serif;color:#1a2e1a;overflow-x:hidden;background:#fff;}
    .fh-c{width:100%;max-width:1280px;margin:0 auto;padding:0 .875rem;}
    @media(min-width:640px){.fh-c{padding:0 1.25rem;}}
    @media(min-width:1024px){.fh-c{padding:0 2rem;}}
    .fh-hdr{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.97);backdrop-filter:blur(12px);border-bottom:1px solid rgba(229,231,235,.6);transition:box-shadow .3s;width:100%;}
    .fh-hdr.sc{box-shadow:0 4px 20px rgba(0,0,0,.07);}
    .fh-hdr-in{display:flex;align-items:center;justify-content:space-between;height:54px;gap:.3rem;width:100%;}
    @media(min-width:768px){.fh-hdr-in{height:66px;}}
    .fh-logo{display:flex;align-items:center;flex-shrink:0;cursor:pointer;}
    .fh-logo img{height:36px;object-fit:contain;}
    @media(min-width:768px){.fh-logo img{height:46px;}}
    .fh-nav{display:none;}
    @media(min-width:960px){.fh-nav{display:flex;align-items:center;gap:1.4rem;flex-shrink:0;}}
    .fh-nl{color:#374151;font-weight:600;font-size:.87rem;text-decoration:none;position:relative;transition:color .2s;background:none;border:none;cursor:pointer;font-family:inherit;padding:0;}
    .fh-nl:hover,.fh-nl:hover::after{color:#059669;width:100%;}
    .fh-nl::after{content:'';position:absolute;bottom:-3px;left:0;width:0;height:2px;background:#059669;transition:width .3s;}
    .fh-acts{display:flex;align-items:center;gap:.28rem;flex-shrink:0;}
    @media(min-width:480px){.fh-acts{gap:.4rem;}}
    .fh-ib{width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:50%;border:none;background:#f3f4f6;color:#374151;cursor:pointer;transition:all .2s;flex-shrink:0;}
    .fh-ib:hover{background:#e5e7eb;color:#059669;} .fh-ib svg{width:.95rem;height:.95rem;}
    @media(min-width:768px){.fh-ib{width:38px;height:38px;} .fh-ib svg{width:1.1rem;height:1.1rem;}}
    .fh-cw{position:relative;display:inline-flex;}
    .fh-cb{position:absolute;top:-4px;right:-4px;width:.95rem;height:.95rem;background:#ef4444;color:white;font-size:.52rem;font-weight:800;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;}
    .fh-sb{display:none;}
    @media(min-width:520px){.fh-sb{display:flex;align-items:center;gap:.3rem;padding:.38rem .8rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:700;font-size:.75rem;border-radius:9999px;border:none;cursor:pointer;transition:all .3s;white-space:nowrap;flex-shrink:0;font-family:'Nunito',sans-serif;}}
    .fh-sb:hover{transform:translateY(-1px);box-shadow:0 6px 12px rgba(16,185,129,.3);} .fh-sb svg{width:.82rem;height:.82rem;}
    .fh-pw{position:relative;display:inline-flex;}
    .fh-pb{display:flex;align-items:center;gap:.3rem;background:#ecfdf5;border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.75rem;padding:.28rem .65rem .28rem .28rem;border-radius:9999px;cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .fh-pb:hover{background:#d1fae5;}
    .fh-pav{width:1.5rem;height:1.5rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.65rem;font-weight:800;flex-shrink:0;}
    .fh-pnm{display:none;max-width:65px;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:440px){.fh-pnm{display:block;}}
    .fh-lbtn{display:flex;align-items:center;gap:.28rem;background:white;color:#059669;border:1.5px solid #d1fae5;font-weight:700;font-size:.72rem;padding:.32rem .65rem;border-radius:9999px;cursor:pointer;transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;flex-shrink:0;}
    .fh-lbtn:hover{background:#f0fdf4;} .fh-lbtn svg{width:.75rem;height:.75rem;}
    .fh-mm{position:fixed;inset:0;z-index:150;pointer-events:none;} .fh-mm.open{pointer-events:all;}
    .fh-mo{position:absolute;inset:0;background:rgba(0,0,0,0);transition:background .3s;} .fh-mm.open .fh-mo{background:rgba(0,0,0,.45);}
    .fh-md{position:absolute;top:0;right:0;width:min(290px,85vw);height:100%;background:white;transform:translateX(100%);transition:transform .35s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;box-shadow:-8px 0 30px rgba(0,0,0,.1);}
    .fh-mm.open .fh-md{transform:translateX(0);}
    .fh-mh{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;border-bottom:1px solid #f3f4f6;}
    .fh-mn{flex:1;padding:.65rem 0;overflow-y:auto;}
    .fh-ml{display:flex;align-items:center;padding:.75rem 1.1rem;font-size:.92rem;font-weight:600;color:#374151;text-decoration:none;border-left:3px solid transparent;transition:all .2s;background:none;border-top:none;border-right:none;border-bottom:none;width:100%;cursor:pointer;font-family:inherit;}
    .fh-ml:hover{background:#f0fdf4;color:#059669;border-left-color:#059669;}
    .fh-mub{padding:.8rem 1rem;background:#f0fdf4;border-bottom:1px solid #d1fae5;}
    .fh-mav{width:2.1rem;height:2.1rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.9rem;font-weight:800;margin-bottom:.38rem;}
    .fh-mnm{font-weight:800;font-size:.88rem;color:#065f46;} .fh-mem{font-size:.7rem;color:#047857;margin-top:.08rem;}
    .fh-hero{position:relative;background:linear-gradient(150deg,#f0fdf4,#ffffff 50%,#f0f9ff);padding:1.75rem 0 2.25rem;overflow:hidden;}
    @media(min-width:768px){.fh-hero{padding:3.5rem 0 4.5rem;}}
    .fh-hg{display:grid;grid-template-columns:1fr;gap:1.75rem;align-items:start;width:100%;}
    @media(min-width:1024px){.fh-hg{grid-template-columns:1fr 1fr;gap:3rem;align-items:center;}}
    .fh-blob{position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none;z-index:0;}
    .fh-b1{width:15rem;height:15rem;background:#a7f3d0;opacity:.13;top:-3rem;left:-3rem;}
    .fh-b2{width:12rem;height:12rem;background:#99f6e4;opacity:.1;bottom:-2rem;right:-2rem;}
    .fh-hc{position:relative;z-index:2;width:100%;}
    .fh-badge{display:inline-flex;align-items:center;gap:.45rem;background:#ecfdf5;border:1px solid #a7f3d0;color:#047857;font-size:.72rem;font-weight:700;padding:.28rem .85rem;border-radius:9999px;margin-bottom:.9rem;}
    .fh-ht{font-size:1.85rem;font-weight:900;line-height:1.15;margin-bottom:.9rem;color:#111827;}
    @media(min-width:600px){.fh-ht{font-size:2.5rem;}} @media(min-width:1024px){.fh-ht{font-size:3rem;}}
    .fh-gt{background:linear-gradient(135deg,#059669,#0891b2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
    .fh-hs{font-size:.9rem;color:#4b5563;line-height:1.65;margin-bottom:1.5rem;}
    .fh-bg{display:flex;flex-wrap:wrap;gap:.55rem;margin-bottom:1.75rem;}
    .fh-btn{display:inline-flex;align-items:center;gap:.4rem;padding:.6rem 1.2rem;border-radius:9999px;font-weight:700;font-size:.85rem;border:none;cursor:pointer;transition:all .3s;white-space:nowrap;font-family:'Nunito',sans-serif;}
    .fh-bp{background:linear-gradient(135deg,#10b981,#059669);color:white;box-shadow:0 4px 14px rgba(16,185,129,.3);}
    .fh-bp:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(16,185,129,.35);}
    .fh-bo{background:white;color:#059669;border:2px solid #d1fae5;}
    .fh-bo:hover{border-color:#10b981;background:#f0fdf4;transform:translateY(-2px);}
    .fh-btn svg{width:.95rem;height:.95rem;}
    .fh-sts{display:flex;flex-wrap:wrap;align-items:center;gap:1rem;}
    .fh-sn{font-size:1.4rem;font-weight:900;color:#111827;line-height:1;}
    .fh-sl{font-size:.65rem;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.4px;margin-top:.12rem;}
    .fh-sd{width:1px;height:1.65rem;background:#e5e7eb;}
    .fh-wl{background:linear-gradient(135deg,#ecfdf5,#f0fdf4);border:1px solid #a7f3d0;border-radius:1rem;padding:.7rem .95rem;margin-bottom:.9rem;display:flex;align-items:center;gap:.65rem;cursor:pointer;}
    .fh-wl:hover{background:#d1fae5;}
    .fh-wlav{width:2.35rem;height:2.35rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.95rem;font-weight:900;flex-shrink:0;}
    .fh-wlt{font-size:.85rem;font-weight:700;color:#065f46;} .fh-wls{font-size:.72rem;color:#047857;margin-top:.08rem;}
    .fh-hp{background:white;border-radius:1.5rem;box-shadow:0 16px 40px -8px rgba(0,0,0,.1);overflow:hidden;border:1px solid #f3f4f6;width:100%;}
    .fh-hph{background:linear-gradient(135deg,#059669,#0891b2);padding:.9rem 1.1rem;display:flex;align-items:center;justify-content:space-between;}
    .fh-hpt{color:white;font-weight:800;font-size:.9rem;}
    .fh-hpb{background:rgba(255,255,255,.22);color:white;font-size:.65rem;font-weight:700;padding:.2rem .6rem;border-radius:9999px;}
    .fh-pp{padding:.75rem;display:grid;grid-template-columns:repeat(2,1fr);gap:.75rem;}
    .fh-ppc{cursor:pointer;transition:all .25s;text-align:center;}
    .fh-ppc:hover{transform:translateY(-3px);}
    .fh-piw{width:100%;aspect-ratio:1/1;display:flex;align-items:center;justify-content:center;overflow:hidden;margin-bottom:.4rem;}
    .fh-pi{width:100%;height:100%;object-fit:contain;display:block;transition:transform .3s;filter:drop-shadow(0 4px 10px rgba(0,0,0,.1));}
    .fh-ppc:hover .fh-pi{transform:scale(1.07);}
    .fh-pe{font-size:2.5rem;line-height:1;}
    .fh-pn{font-size:.72rem;font-weight:700;color:#059669;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .fh-pp2{font-size:.8rem;font-weight:800;color:#111827;margin-top:.05rem;}
    .fh-pf{padding:.65rem 1rem;border-top:1px solid #f3f4f6;display:flex;align-items:center;justify-content:space-between;}
    .fh-pft{font-size:.72rem;color:#6b7280;}
    @keyframes shimmer{0%,100%{opacity:1}50%{opacity:.35}}
    .sk{animation:shimmer 1.4s ease-in-out infinite;background:#f3f4f6;}
    .sk-panel{border-radius:.75rem;height:120px;}
    .fh-tb{background:#f9fafb;border-bottom:1px solid #e5e7eb;padding:.75rem 0;}
    .fh-ti{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.75rem 1.25rem;}
    .fh-tit{display:flex;align-items:center;gap:.4rem;}
    .fh-tic{width:1.5rem;height:1.5rem;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    .fh-tit span{font-size:.76rem;font-weight:600;color:#374151;}
    .fh-sec{padding:2.75rem 0;} @media(min-width:768px){.fh-sec{padding:4.5rem 0;}}
    .fh-sh{text-align:center;max-width:44rem;margin:0 auto 2rem;}
    .fh-sbg{display:inline-block;font-size:.72rem;font-weight:700;padding:.28rem .85rem;border-radius:9999px;margin-bottom:.6rem;letter-spacing:.35px;}
    .fh-g{background:#ecfdf5;color:#047857} .fh-bl{background:#ecfeff;color:#0891b2} .fh-pu{background:#faf5ff;color:#7c3aed}
    .fh-stit{font-size:1.65rem;font-weight:900;color:#111827;line-height:1.2;margin-bottom:.6rem;}
    @media(min-width:768px){.fh-stit{font-size:2.2rem;}}
    .fh-ssub{font-size:.95rem;color:#6b7280;line-height:1.6;}
    .fh-pg{display:grid;grid-template-columns:repeat(2,1fr);gap:1.25rem;}
    @media(min-width:520px){.fh-pg{grid-template-columns:repeat(3,1fr);gap:1.5rem;}}
    @media(min-width:900px){.fh-pg{grid-template-columns:repeat(4,1fr);gap:1.75rem;}}
    @media(min-width:1200px){.fh-pg{grid-template-columns:repeat(5,1fr);}}
    .fh-pc{display:flex;flex-direction:column;align-items:center;text-align:center;cursor:pointer;transition:transform .25s;background:transparent;border:none;padding:0;}
    .fh-pc:hover{transform:translateY(-4px);}
    .fh-pimg{width:100%;aspect-ratio:1/1;display:flex;align-items:center;justify-content:center;overflow:hidden;margin-bottom:.65rem;position:relative;}
    .fh-pir{width:100%;height:100%;object-fit:contain;display:block;transition:transform .4s;filter:drop-shadow(0 6px 16px rgba(0,0,0,.1));}
    .fh-pc:hover .fh-pir{transform:scale(1.06);}
    .fh-pie{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:4rem;}
    .fh-pbp{position:absolute;top:.3rem;left:.3rem;background:#f59e0b;color:white;font-size:.52rem;font-weight:700;padding:.16rem .48rem;border-radius:9999px;z-index:1;}
    .fh-pnm{font-size:.85rem;font-weight:700;color:#059669;margin-bottom:.18rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;}
    .fh-ppc2{font-size:.9rem;font-weight:900;color:#111827;margin-bottom:.45rem;}
    .fh-ca{display:grid;grid-template-columns:1fr 1fr;gap:.3rem;width:100%;}
    .fh-ab{padding:.42rem .12rem;background:#f0fdf4;color:#059669;font-weight:700;font-size:.65rem;border:1.5px solid #d1fae5;border-radius:.5rem;cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;text-align:center;}
    .fh-ab:hover,.fh-ab.ad{background:#059669;color:white;border-color:#059669;}
    .fh-bb{padding:.42rem .12rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:700;font-size:.65rem;border:none;border-radius:.5rem;cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;text-align:center;}
    .fh-bb:hover{transform:translateY(-1px);box-shadow:0 4px 8px rgba(5,150,105,.3);}
    .fh-err{text-align:center;padding:2.5rem 1rem;color:#6b7280;font-size:.9rem;grid-column:1/-1;}
    .fh-usp{background:linear-gradient(145deg,#f3faf7,#fafffe);padding:2.75rem 0;}
    .fh-ug{display:grid;grid-template-columns:1fr;gap:.95rem;margin-top:1.75rem;}
    @media(min-width:520px){.fh-ug{grid-template-columns:repeat(2,1fr);}}
    @media(min-width:1024px){.fh-ug{grid-template-columns:repeat(4,1fr);}}
    .fh-uc{background:white;padding:1.4rem 1.15rem;border-radius:1.1rem;border:1px solid rgba(5,150,105,.08);text-align:center;transition:all .35s;position:relative;overflow:hidden;}
    .fh-uc::before{content:'';position:absolute;top:0;left:0;width:100%;height:3px;background:linear-gradient(90deg,#10b981,#06b6d4);opacity:0;transition:opacity .3s;}
    .fh-uc:hover::before{opacity:1;} .fh-uc:hover{transform:translateY(-6px);box-shadow:0 20px 30px -8px rgba(5,150,105,.12);}
    .fh-ui{width:3.25rem;height:3.25rem;background:#ecfdf5;border-radius:.85rem;display:flex;align-items:center;justify-content:center;margin:0 auto .8rem;transition:all .3s;}
    .fh-uc:hover .fh-ui{background:#d1fae5;transform:scale(1.1) rotate(4deg);} .fh-ui svg{width:1.65rem;height:1.65rem;color:#059669;}
    .fh-ut{font-size:.97rem;font-weight:800;color:#111827;margin-bottom:.32rem;} .fh-ud{font-size:.8rem;color:#6b7280;line-height:1.5;}
    .fh-sg{display:grid;grid-template-columns:1fr;gap:1.1rem;margin-top:2.25rem;}
    @media(min-width:768px){.fh-sg{grid-template-columns:repeat(3,1fr);gap:1.5rem;}}
    .fh-sc2{text-align:center;padding:1.6rem 1.15rem;background:linear-gradient(145deg,#fff,#f9fafb);border-radius:1.5rem;border:1px solid #f3f4f6;transition:all .35s;}
    .fh-sc2:hover{transform:translateY(-8px);box-shadow:0 20px 28px -8px rgba(5,150,105,.1);border-color:rgba(5,150,105,.2);}
    .fh-snum{width:3.75rem;height:3.75rem;border-radius:1rem;display:flex;align-items:center;justify-content:center;margin:0 auto .9rem;font-size:1.5rem;font-weight:900;color:white;box-shadow:0 8px 16px -4px rgba(5,150,105,.3);}
    .fh-sc2 h3{font-size:1.05rem;font-weight:800;color:#111827;margin-bottom:.45rem;} .fh-sc2 p{font-size:.85rem;color:#6b7280;line-height:1.6;}
    .fh-stb{background:linear-gradient(135deg,#0b4f6c,#0a5c6e);padding:2.25rem 0;color:white;}
    .fh-stg{display:grid;grid-template-columns:repeat(2,1fr);gap:1.1rem;text-align:center;}
    @media(min-width:768px){.fh-stg{grid-template-columns:repeat(3,1fr);}}
    .fh-stn{font-size:1.9rem;font-weight:900;background:linear-gradient(135deg,#fff,#e0f2fe);-webkit-background-clip:text;-webkit-text-fill-color:transparent;line-height:1;margin-bottom:.32rem;}
    .fh-stl{font-size:.82rem;opacity:.85;letter-spacing:.35px;}
    .fh-tes{background:#f9fafb;}
    .fh-tc{background:white;border-radius:1.5rem;padding:1.5rem;border:1px solid #f3f4f6;max-width:44rem;margin:0 auto;box-shadow:0 8px 24px rgba(0,0,0,.04);}
    .fh-th{display:flex;align-items:center;gap:.8rem;margin-bottom:.9rem;}
    .fh-ta{width:2.75rem;height:2.75rem;background:#e0f2fe;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:1.4rem;flex-shrink:0;}
    .fh-tname{font-weight:800;color:#111827;margin-bottom:.1rem;}
    .fh-tloc{font-size:.75rem;color:#6b7280;display:flex;align-items:center;gap:.2rem;}
    .fh-stars{display:flex;gap:.16rem;color:#fbbf24;margin-top:.2rem;} .fh-stars svg{width:.82rem;height:.82rem;fill:currentColor;}
    .fh-txt{font-size:.95rem;color:#374151;line-height:1.65;font-style:italic;}
    .fh-dots{display:flex;justify-content:center;gap:.35rem;margin-top:1.35rem;}
    .fh-dot{width:.45rem;height:.45rem;border-radius:9999px;border:none;cursor:pointer;transition:all .3s;}
    .fh-app{background:linear-gradient(135deg,#059669,#0891b2);padding:2.75rem 0;}
    .fh-apg{display:grid;gap:1.75rem;align-items:center;} @media(min-width:1024px){.fh-apg{grid-template-columns:1fr 1fr;gap:3rem;}}
    .fh-apt{font-size:1.65rem;font-weight:900;color:white;line-height:1.2;margin-bottom:.8rem;}
    .fh-aps{color:rgba(255,255,255,.9);font-size:.95rem;line-height:1.65;margin-bottom:1.5rem;}
    .fh-apb{display:flex;flex-wrap:wrap;gap:.6rem;}
    .fh-asb{background:#111827;color:white;padding:.65rem 1.25rem;border-radius:9999px;font-weight:700;font-size:.86rem;display:flex;align-items:center;gap:.5rem;border:none;cursor:pointer;transition:all .3s;font-family:'Nunito',sans-serif;}
    .fh-asb:hover{background:#1f2937;transform:translateY(-2px);} .fh-asb svg{width:.95rem;height:.95rem;}
    .fh-ft{background:#0f172a;color:white;padding:3rem 0 1.75rem;}
    .fh-fg{display:grid;grid-template-columns:1fr;gap:1.75rem;margin-bottom:2.25rem;}
    @media(min-width:580px){.fh-fg{grid-template-columns:repeat(2,1fr);}} @media(min-width:1024px){.fh-fg{grid-template-columns:2fr 1fr 1fr 1.5fr;gap:2.25rem;}}
    .fh-fl{display:flex;align-items:center;gap:.65rem;margin-bottom:1rem;cursor:pointer;}
    .fh-fl img{height:38px;object-fit:contain;}
    .fh-fab{color:#94a3b8;line-height:1.7;font-size:.86rem;margin-bottom:1.15rem;}
    .fh-sr{display:flex;gap:.6rem;}
    .fh-so{width:2rem;height:2rem;background:#1e293b;border-radius:.55rem;display:flex;align-items:center;justify-content:center;color:white;transition:all .3s;text-decoration:none;}
    .fh-so:hover{background:#2563eb;}
    .fh-fct{font-size:.92rem;font-weight:700;color:white;margin-bottom:1rem;display:flex;align-items:center;gap:.32rem;} .fh-fct svg{width:.88rem;height:.88rem;}
    .fh-fls{list-style:none;} .fh-fls li{margin-bottom:.6rem;}
    .fh-fls a{color:#94a3b8;text-decoration:none;font-size:.86rem;transition:all .2s;display:inline-flex;align-items:center;gap:.28rem;}
    .fh-fls a:hover{color:white;transform:translateX(4px);}
    .fh-fcl{list-style:none;} .fh-fcl li{display:flex;align-items:flex-start;gap:.55rem;color:#94a3b8;font-size:.86rem;margin-bottom:.75rem;} .fh-fcl li svg{width:.95rem;height:.95rem;flex-shrink:0;margin-top:.1rem;}
    .fh-nl2{margin-top:1.1rem;} .fh-nll{font-size:.86rem;font-weight:600;color:white;margin-bottom:.6rem;}
    .fh-nr{display:flex;gap:.32rem;}
    .fh-nr input{flex:1;padding:.58rem .85rem;border-radius:9999px;border:1px solid #334155;background:#1e293b;color:white;font-size:.8rem;min-width:0;font-family:inherit;}
    .fh-nr input::placeholder{color:#64748b;}
    .fh-nb{background:#2563eb;color:white;border:none;border-radius:9999px;padding:.58rem .95rem;font-weight:700;font-size:.78rem;cursor:pointer;transition:all .3s;white-space:nowrap;font-family:inherit;}
    .fh-nb:hover{background:#1d4ed8;}
    .fh-fb{border-top:1px solid #1e293b;padding-top:1.6rem;display:flex;flex-direction:column;align-items:center;gap:.8rem;text-align:center;}
    @media(min-width:768px){.fh-fb{flex-direction:row;justify-content:space-between;text-align:left;}}
    .fh-cp{color:#64748b;font-size:.78rem;}
    .fh-fbl{display:flex;flex-wrap:wrap;gap:1.1rem;justify-content:center;}
    .fh-fbl a{color:#94a3b8;text-decoration:none;font-size:.78rem;} .fh-fbl a:hover{color:white;}
    .fh-sl2{color:#f59e0b!important;font-weight:700;display:inline-flex;align-items:center;gap:.28rem;}
    .tc{text-align:center;}
  `;

  const Skeletons = ({n=8}) => (
    <>{Array.from({length:n}).map((_,i)=>(
      <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
        <div className="sk sk-panel" style={{width:'100%',aspectRatio:'1/1',marginBottom:'.6rem'}}/>
        <div className="sk" style={{height:'.55rem',width:'80%',borderRadius:'9999px',marginBottom:'.3rem'}}/>
        <div className="sk" style={{height:'.55rem',width:'55%',borderRadius:'9999px'}}/>
      </div>
    ))}</>
  );

  const ProductCard = ({ p }) => {
    const imgSrc = getImageSrc(p);
    return (
      <div className="fh-pc" onClick={()=>navigate('/products')}>
        <div className="fh-pimg">
          {p.badge&&<span className="fh-pbp">{p.badge}</span>}
          {imgSrc&&!imgErrors[p.id]
            ?<img className="fh-pir" src={imgSrc} alt={p.name} onError={()=>handleImgError(p.id)}/>
            :<span className="fh-pie">{getEmoji(p)}</span>}
        </div>
        <div className="fh-pnm" title={p.name}>{p.name}</div>
        <div className="fh-ppc2">{p.displayPrice||`₹${p.price}`}</div>
        <div className="fh-ca">
          <button className={`fh-ab${addedMap[p.id]?' ad':''}`} onClick={e=>handleAddToCart(p,e)}>{addedMap[p.id]?'✓ Added!':'+ Cart'}</button>
          <button className="fh-bb" onClick={e=>handleBuyNow(p,e)}>Buy Now</button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{css}</style>
      {showAuthModal && <WhatsAppAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      {showProfile   && <CustomerProfile onClose={()=>setShowProfile(false)}/>}
      {showSearch    && <SearchOverlay products={products} onClose={()=>setShowSearch(false)} navigate={navigate}/>}

      <div>
        {/* HEADER */}
        <header className={`fh-hdr${scrolled?' sc':''}`}>
          <div className="fh-c">
            <div className="fh-hdr-in">
              <div className="fh-logo" onClick={()=>navigate('/')}>
                <img src="/images/logo.png" alt="Fresh Hills" onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/46?text=FH';}}/>
              </div>
              <nav className="fh-nav">
                <a href="#home" className="fh-nl">Home</a>
                <button onClick={()=>navigate('/products')} className="fh-nl">Products</button>
                <a href="#how-it-works" className="fh-nl">How It Works</a>
                <a href="#contact" className="fh-nl">Contact</a>
              </nav>
              <div className="fh-acts">
                <button className="fh-ib" onClick={()=>setShowSearch(true)}><MagnifyingGlassIcon/></button>
                <div className="fh-cw">
                  <button className="fh-ib" onClick={()=>navigate('/cart')}><ShoppingCartIcon/></button>
                  {cartCount>0&&<span className="fh-cb">{cartCount>9?'9+':cartCount}</span>}
                </div>
                {isLoggedIn?(
                  <div className="fh-pw" onClick={e=>{e.stopPropagation();setShowProfileDrop(p=>!p);}}>
                    <button className="fh-pb">
                      <div className="fh-pav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                      <span className="fh-pnm">{(currentUser.name||currentUser.phone||'').split(' ')[0]}</span>
                    </button>
                    {showProfileDrop&&(
                      <ProfileDropdown
                        user={currentUser}
                        onLogout={handleLogout}
                        onViewProfile={()=>{setShowProfileDrop(false);setShowProfile(true);}}
                        onClose={()=>setShowProfileDrop(false)}
                      />
                    )}
                  </div>
                ):(
                  <button className="fh-lbtn" onClick={()=>setShowAuthModal(true)}><UserIcon/>Login</button>
                )}
                <button className="fh-sb" onClick={()=>navigate('/products')}><ShoppingBagIcon/>Shop Now</button>
                <button className="fh-ib" onClick={()=>setMobileMenuOpen(true)}><Bars3Icon/></button>
              </div>
            </div>
          </div>
        </header>

        {/* MOBILE DRAWER */}
        <div className={`fh-mm${mobileMenuOpen?' open':''}`}>
          <div className="fh-mo" onClick={()=>setMobileMenuOpen(false)}/>
          <div className="fh-md">
            <div className="fh-mh">
              <div className="fh-logo"><img src="/images/logo.png" alt="Fresh Hills" style={{height:'30px'}} onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/30?text=FH';}}/></div>
              <button className="fh-ib" onClick={()=>setMobileMenuOpen(false)}><XMarkIcon/></button>
            </div>
            {isLoggedIn&&(
              <div className="fh-mub">
                <div className="fh-mav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                <div className="fh-mnm">{currentUser.name||'Customer'}</div>
                <div className="fh-mem">📱 +{currentUser.phone}</div>
              </div>
            )}
            <nav className="fh-mn">
              {[{label:'Home',href:'#home'},{label:'Products',route:'/products'},{label:'How It Works',href:'#how-it-works'},{label:'Contact',href:'#contact'}].map(item=>
                item.route
                  ?<button key={item.label} className="fh-ml" onClick={()=>{setMobileMenuOpen(false);navigate(item.route);}}>{item.label}</button>
                  :<a key={item.label} href={item.href} className="fh-ml" onClick={()=>setMobileMenuOpen(false)}>{item.label}</a>
              )}
              <button className="fh-ml" style={{color:'#059669'}} onClick={()=>{setMobileMenuOpen(false);setShowSearch(true);}}>Search Products</button>
              {isLoggedIn?(
                <>
                  <button className="fh-ml" style={{color:'#059669'}} onClick={()=>{setMobileMenuOpen(false);setShowProfile(true);}}>👤 My Profile & Orders</button>
                  <button className="fh-ml" style={{color:'#ef4444'}} onClick={()=>{setMobileMenuOpen(false);handleLogout();}}>Logout</button>
                </>
              ):(
                <button className="fh-ml" style={{color:'#059669'}} onClick={()=>{setMobileMenuOpen(false);setShowAuthModal(true);}}>Login / Register</button>
              )}
            </nav>
            <div style={{padding:'.8rem 1rem',borderTop:'1px solid #f3f4f6'}}>
              <button className="fh-btn fh-bp" style={{width:'100%',justifyContent:'center'}} onClick={()=>{setMobileMenuOpen(false);navigate('/products');}}>
                <ShoppingBagIcon style={{width:'1rem',height:'1rem'}}/> Shop Now
              </button>
            </div>
          </div>
        </div>

        {/* HERO */}
        <section id="home" className="fh-hero">
          <div className="fh-blob fh-b1"/><div className="fh-blob fh-b2"/>
          <div className="fh-c fh-hc">
            <div className="fh-hg">
              <div>
                {isLoggedIn&&(
                  <div className="fh-wl" onClick={()=>setShowProfile(true)}>
                    <div className="fh-wlav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="fh-wlt">Welcome back, {(currentUser.name||'there').split(' ')[0]}! 👋</div>
                      <div className="fh-wls">Tap to view your profile & orders →</div>
                    </div>
                  </div>
                )}
                <div className="fh-badge">🌿 100% Nadan · Pure Kerala Taste</div>
                <h1 className="fh-ht"><span className="fh-gt">Kerala's Most Trusted</span><br/>Nadan Milk Store</h1>
                <p className="fh-hs">Pure nadan cow milk, fresh paneer, thick curd and authentic Kerala milk products — delivered fresh to your doorstep every morning.</p>
                <div className="fh-bg">
                  <button className="fh-btn fh-bp" onClick={()=>navigate('/products')}><ShoppingBagIcon/>Shop Products<ArrowRightIcon/></button>
                  {!isLoggedIn
                    ?<button className="fh-btn fh-bo" onClick={()=>setShowAuthModal(true)}><UserIcon/>Login / Register</button>
                    :<button className="fh-btn fh-bo" onClick={()=>navigate('/products')}><TruckIcon/>Order Now</button>}
                </div>
                <div className="fh-sts">
                  <div><div className="fh-sn">10k+</div><div className="fh-sl">Happy Families</div></div>
                  <div className="fh-sd"/>
                  <div><div className="fh-sn">24/7</div><div className="fh-sl">Delivery</div></div>
                </div>
              </div>

              {/* Mini product panel */}
              <div className="fh-hp">
                <div className="fh-hph">
                  <span className="fh-hpt">🥛 Today's Fresh Stock</span>
                  <span className="fh-hpb">● Live</span>
                </div>
                <div className="fh-pp">
                  {loading
                    ?Array.from({length:4}).map((_,i)=><div key={i} className="sk sk-panel" style={{animationDelay:`${i*.1}s`}}/>)
                    :products.slice(0,4).map(p=>{
                      const img=getImageSrc(p);
                      return(
                        <div key={p.id} className="fh-ppc" onClick={()=>navigate('/products')}>
                          <div className="fh-piw">
                            {img&&!imgErrors[`h_${p.id}`]
                              ?<img className="fh-pi" src={img} alt={p.name} onError={()=>handleImgError(`h_${p.id}`)}/>
                              :<span className="fh-pe">{getEmoji(p)}</span>}
                          </div>
                          <div className="fh-pn" title={p.name}>{p.name}</div>
                          <div className="fh-pp2">{p.displayPrice||`₹${p.price}`}</div>
                        </div>
                      );
                    })}
                </div>
                <div className="fh-pf">
                  <span className="fh-pft">{loading?'Loading…':`${products.length} items available`}</span>
                  <button className="fh-btn fh-bp" style={{padding:'.38rem .9rem',fontSize:'.75rem'}} onClick={()=>navigate('/products')}>View All</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <div className="fh-tb">
          <div className="fh-c">
            <div className="fh-ti">
              {[{icon:<BoltIcon style={{width:'.88rem',height:'.88rem',color:'#059669'}}/>,bg:'#d1fae5',label:'Same-Day Delivery'},
                {icon:<CurrencyRupeeIcon style={{width:'.88rem',height:'.88rem',color:'#2563eb'}}/>,bg:'#dbeafe',label:'Cash & Online Pay'},
                {icon:<CubeIcon style={{width:'.88rem',height:'.88rem',color:'#d97706'}}/>,bg:'#fef3c7',label:'Nadan Stock Daily'},
                {icon:<UserGroupIcon style={{width:'.88rem',height:'.88rem',color:'#9333ea'}}/>,bg:'#fae8ff',label:'10k+ Families'}].map(t=>(
                <div key={t.label} className="fh-tit">
                  <div className="fh-tic" style={{background:t.bg}}>{t.icon}</div><span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PRODUCTS */}
        <section id="products" className="fh-sec" style={{background:'#f9fafb'}}>
          <div className="fh-c">
            <div className="fh-sh">
              <span className="fh-sbg fh-g">🛒 OUR PRODUCTS</span>
              <h2 className="fh-stit">Fresh From Kerala Farms</h2>
              <p className="fh-ssub">Hand-picked nadan milk, curd, paneer and milk products sourced directly from Kerala farms.</p>
            </div>
            <div className="fh-pg">
              {loading?<Skeletons n={8}/>
               :loadError?<div className="fh-err"><div style={{fontSize:'2rem',marginBottom:'.5rem'}}>⚠️</div><div>Could not load products.</div><button className="fh-btn fh-bp" style={{marginTop:'.8rem'}} onClick={()=>window.location.reload()}>Retry</button></div>
               :products.length===0?<div className="fh-err">📦 No products yet.</div>
               :products.slice(0,10).map(p=><ProductCard key={p.id} p={p}/>)}
            </div>
            {!loading&&!loadError&&products.length>0&&(
              <div className="tc" style={{marginTop:'2rem'}}>
                <button className="fh-btn fh-bp" onClick={()=>navigate('/products')}>View All {products.length} Products <ArrowRightIcon style={{width:'1rem',height:'1rem'}}/></button>
              </div>
            )}
          </div>
        </section>

        {/* USP */}
        <section className="fh-usp">
          <div className="fh-c">
            <div className="fh-sh"><span className="fh-sbg fh-g">⚡ WHY CHOOSE US</span><h2 className="fh-stit">More Than Just Fresh</h2><p className="fh-ssub">Pure nadan taste with the best technology and trust.</p></div>
            <div className="fh-ug">
              {[{icon:<TruckIcon/>,title:'Fast Delivery',desc:'Same-day delivery across Kerala.'},
                {icon:<ClockIcon/>,title:'24/7 Shopping',desc:'Order anytime — we never sleep.'},
                {icon:<SparklesIcon/>,title:'Nadan & Pure',desc:'Direct from Kerala farms, stocked twice daily.'},
                {icon:<GiftIcon/>,title:'Rewards',desc:'Earn cashback on every order.'}].map(u=>(
                <div key={u.title} className="fh-uc"><div className="fh-ui">{u.icon}</div><div className="fh-ut">{u.title}</div><div className="fh-ud">{u.desc}</div></div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="fh-sec" style={{background:'white'}}>
          <div className="fh-c">
            <div className="fh-sh"><span className="fh-sbg fh-bl">⚡ SIMPLE PROCESS</span><h2 className="fh-stit">How It Works</h2><p className="fh-ssub">Three simple steps to fresh nadan products.</p></div>
            <div className="fh-sg">
              {[{num:'1',bg:'linear-gradient(135deg,#10b981,#059669)',title:'Browse & Select',desc:'Visit our store, browse fresh nadan products and add to cart.'},
                {num:'2',bg:'linear-gradient(135deg,#06b6d4,#0891b2)',title:'Checkout & Pay',desc:'Pay via UPI, card, net banking or cash on delivery.'},
                {num:'3',bg:'linear-gradient(135deg,#8b5cf6,#7c3aed)',title:'Receive & Enjoy',desc:'We deliver farm-fresh nadan products right to your door!'}].map(s=>(
                <div key={s.num} className="fh-sc2"><div className="fh-snum" style={{background:s.bg}}>{s.num}</div><h3>{s.title}</h3><p>{s.desc}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className="fh-stb">
          <div className="fh-c">
            <div className="fh-stg">
              {[{num:'10K+',lbl:'Happy Families'},{num:'2x',lbl:'Faster Delivery'},{num:'24/7',lbl:'Availability'}].map(s=>(
                <div key={s.lbl}><div className="fh-stn">{s.num}</div><div className="fh-stl">{s.lbl}</div></div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="fh-sec fh-tes">
          <div className="fh-c">
            <div className="fh-sh"><span className="fh-sbg fh-pu">💬 CUSTOMER LOVE</span><h2 className="fh-stit">What Our Customers Say</h2></div>
            <div className="fh-tc">
              <div className="fh-th">
                <div className="fh-ta">{testimonials[activeTestimonial].image}</div>
                <div>
                  <div className="fh-tname">{testimonials[activeTestimonial].name}</div>
                  <div className="fh-tloc"><MapPinIcon style={{width:'.68rem',height:'.68rem'}}/>{testimonials[activeTestimonial].location}</div>
                  <div className="fh-stars">{Array(5).fill(0).map((_,i)=><StarIcon key={i}/>)}</div>
                </div>
              </div>
              <p className="fh-txt">"{testimonials[activeTestimonial].text}"</p>
              <div className="fh-dots">{testimonials.map((_,i)=><button key={i} className="fh-dot" onClick={()=>setActiveTestimonial(i)} style={{background:i===activeTestimonial?'#059669':'#d1d5db'}}/>)}</div>
            </div>
          </div>
        </section>

        {/* APP */}
        <section className="fh-app">
          <div className="fh-c">
            <div className="fh-apg">
              <div>
                <h2 className="fh-apt">Get the Fresh Hills Mobile App</h2>
                <p className="fh-aps">Shop faster, track orders in real time and get exclusive app-only offers.</p>
                <div className="fh-apb">
                  <button className="fh-asb"><DevicePhoneMobileIcon/> App Store</button>
                  <button className="fh-asb"><DevicePhoneMobileIcon/> Google Play</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer id="contact" className="fh-ft">
          <div className="fh-c">
            <div className="fh-fg">
              <div>
                <div className="fh-fl" onClick={()=>navigate('/')}><img src="/images/logo.png" alt="Fresh Hills" onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/38?text=FH';}}/></div>
                <p className="fh-fab">Kerala's trusted nadan milk store. Pure cow milk, fresh paneer, curd and more — delivered fresh every morning since 2020.</p>
                <div className="fh-sr">{['fb','tw','ig'].map(s=><a key={s} href="#" className="fh-so"><svg fill="currentColor" viewBox="0 0 24 24" style={{width:'1rem',height:'1rem'}}><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/></svg></a>)}</div>
              </div>
              <div>
                <h4 className="fh-fct"><HeartIcon/>Quick Links</h4>
                <ul className="fh-fls">
                  {[{label:'Home',href:'#home'},{label:'Products',route:'/products'},{label:'How It Works',href:'#how-it-works'},{label:'Contact',href:'#contact'}].map(l=>(
                    <li key={l.label}>{l.route?<a href="#" onClick={e=>{e.preventDefault();navigate(l.route);}}><ChevronRightIcon style={{width:'.62rem'}}/>{l.label}</a>:<a href={l.href}><ChevronRightIcon style={{width:'.62rem'}}/>{l.label}</a>}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-fct"><HeartIcon/>Our Products</h4>
                <ul className="fh-fls">
                  {['Nadan Cow Milk','Fresh Paneer','Curd & Yogurt','Buttermilk','Ghee'].map(p=>(
                    <li key={p}><a href="#" onClick={e=>{e.preventDefault();navigate('/products');}}><ChevronRightIcon style={{width:'.62rem'}}/>{p}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-fct"><HeartIcon/>Contact Us</h4>
                <ul className="fh-fcl">
                  <li><MapPinIcon/><span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Ernakulam, Kerala, 682021</span></li>
                  <li><DevicePhoneMobileIcon/><span>+91 70108 44160</span></li>
                  <li><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width:'.95rem',height:'.95rem',flexShrink:0}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span>hello@fesiconenterprises.com</span></li>
                </ul>
                <div className="fh-nl2"><div className="fh-nll">Subscribe for offers</div><div className="fh-nr"><input type="email" placeholder="Your email address"/><button className="fh-nb">Go</button></div></div>
              </div>
            </div>
            <div className="fh-fb">
              <div className="fh-cp">© {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in Kerala<br/><span style={{fontSize:'.68rem',display:'block',marginTop:'.18rem'}}>GST: 32AAGFF6427Q1Z2</span></div>
              <div className="fh-fbl"><a href="#">Privacy Policy</a><a href="#">Terms of Service</a><a href="#">Cookie Policy</a><a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="fh-sl2">SEO9INE <HeartIcon style={{width:'.78rem',height:'.78rem',color:'#ef4444'}}/></a></div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;